/**
 * Delivery rows with a plain-language result. Used by the delivery log, the customer page and
 * the message page. Opening a row shows the DeliveryDrawer.
 */
import type { Delivery, DeliveryLogItem } from "@aiyo/shared";
import { Table } from "@heroui/react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "../lib/format";
import { IssueChip } from "./issues";
import { ChannelChip, StateChip } from "./ui";

type Row = Delivery & Partial<Pick<DeliveryLogItem, "app" | "preview" | "test_mode">>;

export function DeliveryTable({
  rows,
  onOpen,
  label,
  showRecipient = true,
  showMessage = true,
  empty,
}: {
  rows: Row[];
  onOpen: (id: string) => void;
  label: string;
  showRecipient?: boolean;
  showMessage?: boolean;
  empty?: ReactNode;
}) {
  const { t } = useTranslation();
  const fmt = useFormat();
  return (
    <>
      {/* Phones: one card per delivery, result first, so nothing hides behind a horizontal scroll. */}
      <ul className="flex flex-col gap-2 sm:hidden" aria-label={label}>
        {rows.length === 0 ? (
          <li className="rounded-2xl bg-surface p-6 text-center text-sm text-muted">{empty}</li>
        ) : (
          rows.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onOpen(d.id)}
                className="flex w-full flex-col gap-2 rounded-2xl bg-surface p-4 text-left shadow-surface outline-none focus-visible:ring-2 focus-visible:ring-focus"
              >
                <span className="flex items-start justify-between gap-3">
                  <span className="min-w-0 truncate text-sm font-medium">
                    {showRecipient ? (d.external_id ?? t("logs.directAddress")) : (d.preview ?? "—")}
                  </span>
                  <IssueChip issue={d.issue} />
                </span>
                {showRecipient && showMessage && d.preview ? (
                  <span className="truncate text-sm text-muted">{d.preview}</span>
                ) : null}
                <span className="flex items-center gap-2 text-xs text-muted">
                  <ChannelChip channel={d.channel} />
                  {fmt.relative(d.queued_at)}
                  {d.app && showMessage ? <span className="truncate">· {d.app.name}</span> : null}
                </span>
              </button>
            </li>
          ))
        )}
      </ul>
      <div className="hidden sm:block">
        <Table>
          <Table.ScrollContainer>
            <Table.Content
              aria-label={label}
              className="min-w-[760px]"
              onRowAction={(key) => onOpen(String(key))}
            >
              <Table.Header>
                <Table.Column isRowHeader>{t("logs.columns.time")}</Table.Column>
                {showRecipient ? <Table.Column>{t("logs.columns.recipient")}</Table.Column> : null}
                {showMessage ? <Table.Column>{t("logs.columns.message")}</Table.Column> : null}
                <Table.Column>{t("logs.columns.channel")}</Table.Column>
                <Table.Column>{t("logs.columns.result")}</Table.Column>
              </Table.Header>
              <Table.Body
                renderEmptyState={() => <div className="py-10 text-center text-sm text-muted">{empty}</div>}
              >
                {rows.map((d) => (
                  <Table.Row key={d.id} id={d.id} className="cursor-pointer">
                    <Table.Cell className="whitespace-nowrap">
                      <span className="flex flex-col" title={fmt.dateTime(d.queued_at)}>
                        <span className="text-sm">{fmt.relative(d.queued_at)}</span>
                        <span className="text-xs text-muted">{fmt.dateTime(d.queued_at)}</span>
                      </span>
                    </Table.Cell>
                    {showRecipient ? (
                      <Table.Cell>
                        <span className="flex max-w-[240px] flex-col">
                          <span className="truncate text-sm font-medium">
                            {d.external_id ?? (
                              <span className="font-normal text-muted">{t("logs.directAddress")}</span>
                            )}
                          </span>
                          <span className="truncate font-mono text-xs text-muted">
                            {d.address || t("logs.noAddress")}
                          </span>
                        </span>
                      </Table.Cell>
                    ) : null}
                    {showMessage ? (
                      <Table.Cell>
                        <span className="flex max-w-[280px] flex-col">
                          <span className="truncate text-sm">{d.preview ?? "—"}</span>
                          {d.app ? (
                            <span className="flex items-center gap-2 truncate text-xs text-muted">
                              {d.app.name}
                              {d.test_mode ? <StateChip ok="warn" label={t("logs.test")} /> : null}
                            </span>
                          ) : null}
                        </span>
                      </Table.Cell>
                    ) : null}
                    <Table.Cell>
                      <ChannelChip channel={d.channel} />
                    </Table.Cell>
                    <Table.Cell>
                      <IssueChip issue={d.issue} />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      </div>
    </>
  );
}
